<script setup lang="ts">
import { Indicator } from "@types"
import { computed, useTemplateRef } from "vue"

type Props = {
    /**
     * Objet contenant le nom du graphique affichant les données
     * et la liste d'indicateurs de données associée à ce graphique.
     */
    indicators:
        | {
              indicator: Indicator[] | undefined
              name: string
          }[]
        | undefined
    /**
     * Indique si le composant est en cours de chargement.
     */
    loading?: boolean
    /**
     * Indique si une erreur est survenue lors du chargement des données.
     */
    error?: boolean
}
/**
 * Définit les événements que le composant peut émettre.
 * @event click
 */
const emit = defineEmits(["click:kpi"])
const scrollable = useTemplateRef<HTMLDivElement>("scrollable")

/**
 * Gestionnaire de clic pour un indicateur.
 * Émet un événement "click" avec l'identifiant de l'indicateur cliqué.
 *
 * @param {String}
 * @return {void}
 */
const handleClick = (payload: { type: string; graph_id: string }): void => {
    emit("click:kpi", payload)
}

/**
 * Fonction permettant de scroll vers la gauche
 */
const scrollLeft = () => {
    if (scrollable.value) {
        scrollable.value.scrollBy({
            left: -320,
            behavior: "smooth",
        })
    }
}

/**
 * Fonction permettant de scroll vers la droite
 */
const scrollRight = () => {
    if (scrollable.value) {
        scrollable.value.scrollBy({
            left: 320,
            behavior: "smooth",
        })
    }
}
defineProps<Props>()

/**
 * Indique si l'affichage des boutons de scroll est nécessaire
 */
const isNeedScroll = computed(() => {
    return scrollable.value
        ? scrollable.value.clientWidth < scrollable.value.scrollWidth
        : false
})
</script>

<template>
    <div class="data-indicator-list" v-if="!loading && !error">
        <BaseButton
            v-if="isNeedScroll"
            icon="pi pi-angle-left"
            @click="scrollLeft"
            class="data-indicator-list__scroll-button left"
        />
        <div class="data-indicator-list__scrollable" ref="scrollable">
            <DataIndicatorListItem
                class="data-indicator-list__scrollable-item"
                v-for="indicatorItem in indicators"
                :key="indicatorItem.name"
                :indicator-list="indicatorItem.indicator"
                :name="indicatorItem.name"
                @click:kpi="handleClick"
            >
            </DataIndicatorListItem>
        </div>
        <BaseButton
            v-if="isNeedScroll"
            icon="pi pi-angle-right"
            @click="scrollRight"
            class="data-indicator-list__scroll-button right"
        />
    </div>
    <div v-else-if="!loading && error" class="data-indicator-list">
        <BaseMessage closable severity="error">
            <p>
                Erreur lors du chargement des indicateurs de données. Veuillez
                réessayer plus tard.
            </p>
        </BaseMessage>
    </div>
    <div v-else class="data-indicator-list">
        <BaseSkeleton
            v-for="i in 3"
            :key="i"
            width="10rem"
            height="4rem"
        ></BaseSkeleton>
    </div>
</template>

<style scoped>
@import "@styles/breakpoints.css";

.data-indicator-list {
    display: flex;
    position: relative;
    justify-content: space-around;
    .data-indicator-list__scrollable {
        display: flex;
        flex-direction: row;
        gap: 18px;
        overflow-x: auto;
        scroll-behavior: smooth;
        max-width: 90%;
        &::-webkit-scrollbar {
            display: none;
        }
    }
    .data-indicator-list__scroll-button {
        position: absolute;
        top: 0;
        bottom: 0;
        height: 100%;
        z-index: 1;
        border-radius: 6px;
        color: white;
        background-color: var(--p-cyan-400);
        border-color: var(--p-cyan-400);
        &:hover {
            color: white;
            background-color: var(--p-cyan-500);
            border-color: var(--p-cyan-500);
        }
        &.right {
            right: 0;
        }
        &.left {
            left: 0;
        }
    }
}
</style>
